import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, Chip, Stack, IconButton, alpha,
} from '@mui/material';
import { ArrowForward, Person, Place } from '@mui/icons-material';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

type ActivityTab = 'rides' | 'food' | 'grocery' | 'porter' | 'on_demand';

interface ActivityItem {
  _id?: string;
  rideNumber?: string;
  orderNumber?: string;
  bookingNumber?: string;
  status?: string;
  total?: number;
  fare?: { total?: number };
  createdAt?: string;
  customerId?: { firstName?: string; lastName?: string } | string;
  driverId?: { firstName?: string; lastName?: string };
  partnerId?: { firstName?: string; lastName?: string };
  providerId?: { firstName?: string; lastName?: string };
  restaurantId?: { name?: string };
  storeId?: { name?: string };
  pickup?: { address?: string; city?: string; state?: string; pincode?: string };
  dropoff?: { address?: string; city?: string; state?: string; pincode?: string };
  deliveryAddress?: { address?: string; street?: string; city?: string; line1?: string; formattedAddress?: string };
  address?: { address?: string; street?: string; line1?: string; formattedAddress?: string; city?: string } | string;
  stops?: Array<{ type?: string; address?: string; city?: string }>;
}

const TAB_CONFIG: { key: ActivityTab; label: string; listKey: string; path: string; color: string }[] = [
  { key: 'rides', label: 'Rides', listKey: 'recentRides', path: '/hubs/transport/transport/bookings', color: '#2563eb' },
  { key: 'food', label: 'Food', listKey: 'recentOrders', path: '/hubs/delivery/food-delivery/bookings', color: '#ea580c' },
  { key: 'grocery', label: 'Grocery', listKey: 'recentGroceryOrders', path: '/hubs/delivery/grocery-delivery/bookings', color: '#059669' },
  { key: 'porter', label: 'Porter', listKey: 'recentPorterOrders', path: '/hubs/delivery/porter-service/bookings', color: '#0f766e' },
  { key: 'on_demand', label: 'Services', listKey: 'recentOnDemand', path: '/hubs/on_demand/home-services/bookings', color: '#0891b2' },
];

function customerName(item: ActivityItem): string {
  const c = item.customerId;
  if (!c || typeof c === 'string') return 'Customer';
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Customer';
}

function itemRef(item: ActivityItem): string {
  return item.rideNumber || item.orderNumber || item.bookingNumber || String(item._id || '—').slice(-6);
}

function addressText(address?: ActivityItem['address']): string | undefined {
  if (!address) return undefined;
  if (typeof address === 'string') return address;
  return address.formattedAddress || address.address || address.street || address.line1;
}

function itemAmount(item: ActivityItem): number {
  return item.fare?.total ?? item.total ?? 0;
}

function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString();
}

function statusColor(status?: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const s = (status || '').toLowerCase();
  if (['completed', 'delivered', 'confirmed'].includes(s)) return 'success';
  if (['pending', 'searching', 'placed'].includes(s)) return 'warning';
  if (['cancelled', 'failed', 'rejected'].includes(s)) return 'error';
  if (['accepted', 'arrived', 'started', 'preparing', 'delivering', 'in_progress', 'assigned'].includes(s)) return 'info';
  return 'default';
}

function formatPlace(place?: { address?: string; city?: string; state?: string; pincode?: string; street?: string; line1?: string; formattedAddress?: string }): string | undefined {
  if (!place) return undefined;
  const primary = place.formattedAddress || place.address || place.street || place.line1;
  const extra = [place.city, place.state, place.pincode].filter(Boolean).join(', ');
  if (primary && extra && !primary.toLowerCase().includes(extra.toLowerCase())) {
    return `${primary}, ${extra}`;
  }
  return primary || extra || undefined;
}

function locationName(item: ActivityItem, tab: ActivityTab): string | undefined {
  const pickup = formatPlace(item.pickup);
  const dropoff = formatPlace(item.dropoff);
  const delivery = formatPlace(item.deliveryAddress);
  const stopPickup = item.stops?.find((s) => s.type === 'pickup');
  const stopDrop = item.stops?.find((s) => s.type === 'drop' || s.type === 'dropoff');

  if (tab === 'rides') {
    const status = (item.status || '').toLowerCase();
    if (['completed', 'started', 'arrived'].includes(status)) return dropoff || pickup;
    return pickup || dropoff;
  }
  if (tab === 'food' || tab === 'grocery') return delivery;
  if (tab === 'porter') return formatPlace(stopDrop) || formatPlace(stopPickup) || pickup || dropoff;
  return addressText(item.address);
}

function fullLocationDetails(item: ActivityItem, tab: ActivityTab): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  const pickup = formatPlace(item.pickup)
    || formatPlace(item.stops?.find((s) => s.type === 'pickup'));
  const dropoff = formatPlace(item.dropoff)
    || formatPlace(item.stops?.find((s) => s.type === 'drop' || s.type === 'dropoff'));
  const delivery = formatPlace(item.deliveryAddress);
  const serviceAddr = addressText(item.address);

  if (tab === 'rides' || tab === 'porter') {
    if (pickup) rows.push({ label: 'Pickup', value: pickup });
    if (dropoff) rows.push({ label: 'Dropoff', value: dropoff });
  } else if (tab === 'food' || tab === 'grocery') {
    if (delivery) rows.push({ label: 'Delivery', value: delivery });
  } else if (serviceAddr) {
    rows.push({ label: 'Address', value: serviceAddr });
  }
  return rows;
}

interface DashboardRecentActivityProps {
  dashboardData?: Record<string, unknown>;
  loading?: boolean;
  embedded?: boolean;
}

export default function DashboardRecentActivity({ dashboardData, loading, embedded }: DashboardRecentActivityProps) {
  const navigate = useNavigate();
  const { formatAmount } = useTenantCurrency();
  const [tab, setTab] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const config = TAB_CONFIG[tab];
  const items = ((dashboardData?.[config.listKey] as ActivityItem[]) || []).slice(0, 8);

  const body = (
    <>
      {!embedded && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="subtitle1" fontWeight={800}>Recent activity</Typography>
          <IconButton size="small" onClick={() => navigate(config.path)}>
            <ArrowForward fontSize="small" />
          </IconButton>
        </Box>
      )}
      {embedded && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <IconButton size="small" onClick={() => navigate(config.path)}>
            <ArrowForward fontSize="small" />
          </IconButton>
        </Box>
      )}

        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setExpandedId(null);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontWeight: 700, fontSize: '0.75rem' } }}
        >
          {TAB_CONFIG.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>

        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : !items.length ? (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">No recent {config.label.toLowerCase()} activity</Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {items.map((item, index) => {
              const amount = itemAmount(item);
              const location = locationName(item, config.key);
              const itemId = String(item._id || itemRef(item) || index);
              const expanded = expandedId === itemId;
              const details = fullLocationDetails(item, config.key);
              return (
                <Box
                  key={itemId}
                  sx={{
                    py: 1.25,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                    cursor: 'pointer',
                    borderRadius: 1,
                    px: 0.5,
                    bgcolor: expanded ? alpha(config.color, 0.06) : 'transparent',
                    '&:hover': { bgcolor: alpha(config.color, 0.04) },
                  }}
                  onClick={() => setExpandedId(expanded ? null : itemId)}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box minWidth={0} flex={1}>
                      <Stack direction="row" alignItems="center" spacing={0.75} mb={0.35}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary">{index + 1}.</Typography>
                        <Typography variant="body2" fontWeight={700}>#{itemRef(item)}</Typography>
                        {item.status && (
                          <Chip
                            label={String(item.status).replace(/_/g, ' ')}
                            size="small"
                            color={statusColor(item.status)}
                            sx={{ height: 20, fontSize: '0.62rem', textTransform: 'capitalize' }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Person sx={{ fontSize: 13, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">{customerName(item)}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>{formatTime(item.createdAt)}</Typography>
                      </Stack>
                      {location && (
                        <Stack direction="row" alignItems="flex-start" spacing={0.5} mt={0.25}>
                          <Place sx={{ fontSize: 12, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            noWrap={!expanded}
                            sx={{ whiteSpace: expanded ? 'normal' : 'nowrap', wordBreak: expanded ? 'break-word' : undefined }}
                          >
                            {location}
                          </Typography>
                        </Stack>
                      )}
                      {expanded && details.length > 0 && (
                        <Stack spacing={0.35} mt={0.75} pl={2.25}>
                          {details.map((row) => (
                            <Box key={row.label}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                                {row.label}
                              </Typography>
                              <Typography variant="caption" color="text.primary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {row.value}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      {expanded && !details.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, pl: 2.25 }}>
                          No address on this trip
                        </Typography>
                      )}
                    </Box>
                    {amount > 0 && (
                      <Typography variant="body2" fontWeight={800} color="primary.main" sx={{ flexShrink: 0 }}>
                        {formatAmount(amount)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
    </>
  );

  if (embedded) return body;

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, mb: 3, height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        {body}
      </CardContent>
    </Card>
  );
}
